import { useLayoutEffect, useState } from "react";
import { isHandoutWordLayout } from "./handoutWordLayout";

export const measureHandoutChapters = (flow, sections, stride) => {
  if (!flow || !stride) return [];
  const bounds = flow.getBoundingClientRect();
  const scale = flow.offsetWidth > 0 ? bounds.width / flow.offsetWidth : 1;
  if (!(scale > 0)) return [];
  const byId = new Map(sections.filter((section) => section.chapter).map((section) => [String(section.id), section.chapter]));
  return Array.from(flow.querySelectorAll("[data-handout-chapter-start]")).flatMap((marker) => {
    const id = marker.getAttribute("data-handout-chapter-start");
    const chapter = byId.get(id);
    const rect = Array.from(marker.getClientRects()).find((item) => item.width > 0 && item.height > 0);
    if (!chapter || !rect) return [];
    return [{ id, page: Math.max(0, Math.round((rect.left - bounds.left) / scale / stride)),
      title: String(chapter.headerTitle ?? chapter.header_title ?? chapter.title ?? "") }];
  });
};

export const handoutHeaderForPage = (settings, page, chapters = []) => {
  const config = settings.headerFooter || {};
  const even = Boolean((isHandoutWordLayout(settings) || config.differentOddEvenPages) && page % 2);
  const prefix = even ? "even" : "odd";
  const explicit = [config[`${prefix}HeaderLeft`] || "", config[`${prefix}HeaderRight`] || ""];
  if (explicit.some(Boolean) || !isHandoutWordLayout(settings)) return explicit;
  const chapter = chapters.filter((item) => item.page <= page).at(-1);
  return even ? ["", chapter?.title || ""] : [chapter?.title || "", ""];
};

export default function useHandoutChapterHeaders(flowRef, sections, settings, stride) {
  const [chapters, setChapters] = useState([]);
  useLayoutEffect(() => {
    const flow = flowRef.current;
    if (!flow || !isHandoutWordLayout(settings)) return undefined;
    let frame;
    let disposed = false;
    const measure = () => {
      frame = undefined;
      if (disposed) return;
      const next = measureHandoutChapters(flow, sections, stride);
      setChapters((current) => JSON.stringify(current) === JSON.stringify(next) ? current : next);
    };
    const schedule = () => {
      if (disposed) return;
      if (frame === undefined) frame = window.requestAnimationFrame(measure);
    };
    measure();
    const resize = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
    resize?.observe(flow);
    const mutations = typeof MutationObserver === "undefined" ? null : new MutationObserver(schedule);
    mutations?.observe(flow, { childList: true, subtree: true, characterData: true, attributes: true });
    flow.addEventListener("load", schedule, true);
    window.addEventListener("resize", schedule);
    document.fonts?.ready?.then(schedule).catch(() => {});
    return () => {
      disposed = true;
      if (frame !== undefined) window.cancelAnimationFrame(frame);
      resize?.disconnect();
      mutations?.disconnect();
      flow.removeEventListener("load", schedule, true);
      window.removeEventListener("resize", schedule);
    };
  }, [flowRef, sections, settings, stride]);
  return chapters;
}
